import { apiRequest } from './api';
import type { FeeType } from './feeHeadApi';

// ── Types ────────────────────────────────────────────

export type PaymentMethod = 'cash' | 'cheque' | 'online' | 'bank_transfer' | 'pos';

export type TransactionStatus = 'success' | 'pending' | 'failed' | 'refunded';

/**
 * A fee the collector adds to the invoice at collection time.
 *  - `fee_head`  → charge a fee head. Reference an existing head via `fee_head_id`,
 *                  OR create/reuse one by `fee_head_name` (saved to the catalog).
 *                  Supports an inline, one-off discount bound to the head.
 *  - `transport` → charge transport. Fare precedence is amount > allocation/stop/route.
 *                  Supports an inline, one-off discount off the fare.
 */
/**
 * How a fee applies across the months of a multi-month payment. Required when the request
 * carries `months`; rejected when it carries `invoice_id` — a single invoice has nothing
 * to distribute across.
 *
 *  - `split`     — one charge divided evenly over the months (₹300 over 3 → ₹100 each).
 *  - `per_month` — the charge lands on every month (₹300 over 3 → ₹900).
 *  - `once`      — the whole charge on the newest month alone.
 *
 * The server does the division in integer paise, giving the remainder to the oldest
 * months. Never pre-split the amount here.
 */
export type FeeDistribution = 'split' | 'per_month' | 'once';

export type FeeTransactionLineItem =
    | {
        type: 'fee_head';
        fee_head_id?: string;
        fee_head_name?: string;
        fee_head_type?: FeeType;
        amount: number;              // gross, >= 0.01
        description?: string | null;
        is_optional?: boolean;
        discount?: { amount: number; name?: string };
        distribution?: FeeDistribution;
    }
    | {
        type: 'transport';
        transport_allocation_id?: string;
        route_stop_id?: string;
        transport_route_id?: string;
        amount?: number;
        description?: string | null;
        discount?: { amount: number; name?: string };
        distribution?: FeeDistribution;
    };

export type CreateFeeTransactionPayload = {
    student_id: string;
    academic_session_id: string;
    invoice_id: string;
    transaction_date: string;        // YYYY-MM-DD
    amount_paid: number;
    payment_method: PaymentMethod;
    reference_number?: string | null;
    status?: TransactionStatus;
    // Required together when line items are present. `expected_line_items_total`
    // must equal the server-computed net (gross − discounts) of `line_items`.
    expected_line_items_total?: number;
    line_items?: FeeTransactionLineItem[];
};

/**
 * Collect one payment across one or more months.
 *
 * `amount_paid` is the single total handed over — never pre-split it per month. The
 * backend sorts the months by issue date and settles them oldest-first, so the order
 * sent here doesn't matter.
 *
 * Mutually exclusive with `invoice_id` (and therefore with `line_items`, which require
 * one). A month that has never been billed is invoiced as a *side effect* of collecting,
 * so this must not be called speculatively to preview what a month would cost.
 */
export type CreateMonthlyFeeTransactionPayload = {
    student_id: string;
    academic_session_id: string;
    /** YYYY-MM, distinct. */
    months: string[];
    transaction_date: string;        // YYYY-MM-DD
    amount_paid: number;
    payment_method: PaymentMethod;
    reference_number?: string | null;
    status?: TransactionStatus;
    /**
     * Ad-hoc fees spread over the months. Every row must carry a `distribution`.
     * `expected_line_items_total` is the *pre-split* net added across the whole payment
     * (`per_month` rows counted once per month) — the server re-validates it against what
     * it actually created, so the client never replicates the server's rounding.
     */
    line_items?: FeeTransactionLineItem[];
    expected_line_items_total?: number;
};

/** The student as embedded in a fee-transaction record (list + create responses). */
export type TransactionStudent = {
    id: string;
    name: string | null;
    first_name: string | null;
    last_name: string | null;
    enrollment_number: string | null;
    father_name: string | null;
    mother_name: string | null;
    father_contact_number: string | null;
    address: string | null;
    profile_photo_url: string | null;
    class: { id: string; name: string } | null;
    section: { id: string; name: string } | null;
};

/** An invoice line as returned after a payment is recorded (existing + newly added). */
export type FeeTransactionInvoiceItem = {
    id: string;
    fee_head_id: string;
    fee_head_name: string;
    description: string | null;
    gross_amount: string;
    applied_discount_id: string | null;
    discount_amount: string;
    net_amount: string;
    is_optional: boolean;
};

export type FeeTransactionResult = {
    id: string;
    organization_id: string;
    student_id: string;
    academic_session_id: string;
    invoice_id: string;
    transaction_date: string;
    amount_paid: string;
    payment_method: PaymentMethod;
    reference_number: string | null;
    status: TransactionStatus;
    recorded_by_staff_id: string | null;
    created_at: string;
    student: TransactionStudent;
    invoice: {
        id: string;
        invoice_number: string;
        title: string;
        total_amount: string;
        balance_amount: string;
        /** Null when the server does not expand the invoice lines. */
        items: FeeTransactionInvoiceItem[] | null;
    };
};

/**
 * How much of a payment landed on one invoice. The authoritative record of which month
 * got what — read this to render the receipt, never the `months` that were requested.
 *
 * A requested month that received nothing is *absent* from the list (a short payment
 * runs out before it), so `allocations.length` can be smaller than `months.length`.
 * A missing month simply went unpaid.
 */
/** A line on an invoice touched by a months payment. */
export type FeeAllocationItem = {
    fee_head_name: string;
    description: string | null;
    gross_amount: string;
    discount_amount: string;
    net_amount: string;
    /**
     * Which `line_items` row of *this request* created the item, or null when the line was
     * already on the invoice. A `split` fee shows up as one item per month, all carrying
     * the same index — that's how the receipt puts them back together as one charge.
     */
    line_item_index: number | null;
};

export type FeeAllocation = {
    invoice_id: string;
    invoice_number: string;
    title: string;
    /** YYYY-MM */
    month: string;
    amount_allocated: string;
    invoice_total_amount: string;
    /** What the invoice still owes after this payment. */
    invoice_balance_amount: string;
    invoice_status: string;
    /** The invoice's lines. Absent on a server that predates the split-fee change. */
    items?: FeeAllocationItem[];
};

export type MonthlyFeeTransactionResult = {
    id: string;
    student_id: string;
    amount_paid: string;
    payment_method: PaymentMethod;
    status: TransactionStatus;
    transaction_date: string;
    /** Money received but attributed to no invoice — an overpayment, held as an advance.
        "0.00" when the whole amount landed on invoices. Show it; don't swallow it. */
    unallocated_amount: string;
    allocations: FeeAllocation[];
    /** Not part of the documented response for this mode — callers must be able to fall
        back to the student they already have on screen. */
    student?: TransactionStudent;
    reference_number?: string | null;
    created_at?: string;
};

export type TransactionApiRecord = {
    id: string;
    student_id: string;
    invoice_id: string;
    academic_session_id: string;
    transaction_date: string;
    amount_paid: string;
    payment_method: PaymentMethod;
    reference_number: string | null;
    created_at: string;
    updated_at: string;
    student: TransactionStudent;
    /** A payment collected across months settles many invoices, so it may name none. */
    invoice: {
        id: string;
        invoice_number: string;
        title: string;
    } | null;
    /**
     * Every invoice this payment settled — present only on a months payment, and only on a
     * server that exposes it here. Without it a reprint can show just the one invoice the
     * record names, which for a three-month payment is a receipt for a third of the money.
     */
    allocations?: FeeAllocation[];
    /** Overpayment held as an advance. "0.00" when the whole amount landed on invoices. */
    unallocated_amount?: string;
};

export type TransactionListMeta = {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
};

export type TransactionListResponse = {
    data: TransactionApiRecord[];
    meta: TransactionListMeta;
};

// ── API Functions ─────────────────────────────────────

export async function fetchTransactions(params?: {
    student_id?: string;
    student_name?: string;
    class_id?: string;
    section_id?: string;
    invoice_id?: string;
    month?: string; // YYYY-MM
    per_page?: number;
    page?: number;
}): Promise<TransactionListResponse> {
    const query = new URLSearchParams();
    if (params?.student_id) query.set('student_id', params.student_id);
    if (params?.student_name) query.set('student_name', params.student_name);
    if (params?.class_id) query.set('class_id', params.class_id);
    if (params?.section_id) query.set('section_id', params.section_id);
    if (params?.invoice_id) query.set('invoice_id', params.invoice_id);
    if (params?.month) query.set('month', params.month);
    if (params?.per_page) query.set('per_page', String(params.per_page));
    if (params?.page) query.set('page', String(params.page));
    const qs = query.toString();

    const res = await apiRequest<TransactionApiRecord[]>(
        `/api/fee-transactions${qs ? `?${qs}` : ''}`
    );

    return {
        data: res.data,
        meta: (res as unknown as { meta: TransactionListMeta }).meta,
    };
}

/**
 * One transaction, with whatever the server expands on it — `allocations` when the payment
 * was collected across months.
 *
 * Only worth calling when the list record didn't already carry them. A server that has no
 * such endpoint 404s, which callers must treat as "no allocations to be had" and fall back
 * to the invoice the record names.
 */
export async function fetchTransactionById(id: string): Promise<TransactionApiRecord> {
    const res = await apiRequest<TransactionApiRecord>(`/api/fee-transactions/${id}`);
    return res.data;
}

/**
 * Record a payment. When `line_items` are present the backend appends them to the
 * invoice (creating any new fee heads / discounts) and re-validates the total
 * against `expected_line_items_total`. Returns the transaction with the refreshed
 * invoice (grown total + all line items) so callers can print an accurate receipt.
 */
export async function createFeeTransaction(
    payload: CreateFeeTransactionPayload
): Promise<FeeTransactionResult> {
    const res = await apiRequest<FeeTransactionResult>('/api/fee-transactions', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
    return res.data;
}

/**
 * Record a payment across one or more months. Same endpoint as `createFeeTransaction`,
 * but the months form of the request — it returns `allocations` (what each month got)
 * and `unallocated_amount` instead of a single invoice.
 */
export async function createMonthlyFeeTransaction(
    payload: CreateMonthlyFeeTransactionPayload
): Promise<MonthlyFeeTransactionResult> {
    const res = await apiRequest<MonthlyFeeTransactionResult>('/api/fee-transactions', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
    return res.data;
}
